/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-lonely-if */

// Dyrektywy: v-decimal / v-integer (+ modyfikator .unsigned)
// Opcje (binding.value):
// {
//   precision: number|null
//   min: number|undefined
//   max: number|undefined
//   step: number|undefined
//   commaAsDecimal: boolean
//   dotAsDecimal: boolean
//   normalizeTo: 'dot'|'comma'
//   spinButtons: 'vertical'|'horizontal'|false
//   holdDelayMs: number
//   holdStepMs: number
// }

const directiveInstances = new WeakMap();
const pendingBindings = new WeakMap();

class NumericDirectiveInstance {
    constructor(inputElement, binding) {
        this.inputElement = inputElement;
        this.binding = binding;

        this.wrapperElement = null;
        this.buttonUp = null;
        this.buttonDown = null;
        this.holdTimer = null;
        this.repeatTimer = null;
        this.isComposing = false;

        this.onKeydown = this.onKeydown.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onPaste = this.onPaste.bind(this);
        this.onCompositionStart = this.onCompositionStart.bind(this);
        this.onCompositionEnd = this.onCompositionEnd.bind(this);

        inputElement.addEventListener('keydown', this.onKeydown);
        inputElement.addEventListener('input', this.onInput);
        inputElement.addEventListener('change', this.onChange);
        inputElement.addEventListener('paste', this.onPaste);
        inputElement.addEventListener('compositionstart', this.onCompositionStart);
        inputElement.addEventListener('compositionend', this.onCompositionEnd);

        // --- obserwator zmian disabled/readonly ---
        this.attrObserver = new MutationObserver(() => {
            this.refreshSpinButtonsDisabledState();
        });
        this.attrObserver.observe(inputElement, {
            attributes: true,
            attributeFilter: ['disabled', 'readonly'],
        });

        this.createSpinButtonsIfNeeded();
    }

    // --- cykl życia
    updateBinding(binding) {
        const previousOptions = this.getOptions();
        this.binding = binding;
        const currentOptions = this.getOptions();

        if (previousOptions.spinButtons !== currentOptions.spinButtons) {
            this.destroySpinButtons();
            this.createSpinButtonsIfNeeded();
        } else {
            this.refreshSpinButtonsPosition();
            this.refreshSpinButtonsDisabledState();
        }
    }

    destroy() {
        this.inputElement.removeEventListener('keydown', this.onKeydown);
        this.inputElement.removeEventListener('input', this.onInput);
        this.inputElement.removeEventListener('change', this.onChange);
        this.inputElement.removeEventListener('paste', this.onPaste);
        this.inputElement.removeEventListener('compositionstart', this.onCompositionStart);
        this.inputElement.removeEventListener('compositionend', this.onCompositionEnd);
        this.destroySpinButtons();

        if (this.attrObserver) {
            this.attrObserver.disconnect();
            this.attrObserver = null;
        }
    }

    // ---- helpers ----
    isInteractive() {
        const inputEl = this.inputElement;
        // disabled, readOnly lub odziedziczone przez <fieldset disabled>
        const inDisabledFieldset = !!(inputEl.closest && inputEl.closest('fieldset[disabled]'));
        return !(inputEl.disabled || inputEl.readOnly || inDisabledFieldset);
    }

    getOptions() {
        const isDecimal = this.binding.name === 'decimal';
        // eslint-disable-next-line prefer-const, max-len
        let { precision = isDecimal ? null : 0, min, max, step, commaAsDecimal = true, dotAsDecimal = true, normalizeTo = 'dot', spinButtons = 'horizontal', holdDelayMs = 350, holdStepMs = 60 } = this.binding.value || {};

        const isUnsigned = !!this.binding.modifiers.unsigned;

        const toFiniteNumberOrUndefined = value => {
            if (value === null || value === undefined) return undefined;
            if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
            if (typeof value === 'string') {
                const str = value.trim();
                if (!str) return undefined; // "" -> undefined (nie 0)
                const num = Number(str);
                return Number.isFinite(num) ? num : undefined; // "10" -> 10, "null"/"abc" -> undefined
            }
            return undefined;
        };

        min = toFiniteNumberOrUndefined(min);
        max = toFiniteNumberOrUndefined(max);
        step = toFiniteNumberOrUndefined(step);
        precision = toFiniteNumberOrUndefined(precision);

        let effectiveStep = step;
        if (!(typeof effectiveStep === 'number' && effectiveStep > 0)) {
            if (isDecimal && typeof precision === 'number' && precision > 0) {
                effectiveStep = 10 ** -precision;
            } else {
                effectiveStep = 1;
            }
        }

        // solidny clamp nawet gdy min > max
        let minOut = min;
        let maxOut = max;
        if (typeof minOut === 'number' && typeof maxOut === 'number' && minOut > maxOut) {
            const temp = minOut;
            minOut = maxOut;
            maxOut = temp;
        }

        const spinMode = spinButtons === false ? false : spinButtons === 'horizontal' ? 'horizontal' : 'vertical';

        return {
            isDecimal,
            isUnsigned,
            precision: typeof precision === 'number' && precision >= 0 ? precision : null,
            min: minOut,
            max: maxOut,
            step: effectiveStep,
            commaAsDecimal: !!commaAsDecimal,
            dotAsDecimal: !!dotAsDecimal,
            normalizeTo: normalizeTo === 'comma' ? 'comma' : 'dot',
            spinButtons: spinMode,
            holdDelayMs: Number(holdDelayMs) || 350,
            holdStepMs: Number(holdStepMs) || 60,
        };
    }

    // ---------- SPIN BUTTONS UI ----------
    createSpinButtonsIfNeeded() {
        const options = this.getOptions();
        if (!options.spinButtons) return;

        const hostInput = this.inputElement;

        // enforce text/inputmode to avoid locale formatting from <input type="number">
        if (hostInput.type !== 'text') hostInput.setAttribute('type', 'text');
        hostInput.setAttribute('inputmode', 'decimal');
        hostInput.setAttribute('autocomplete', 'off'); // wyłączenie autouzupełniania

        if (!hostInput.parentNode) return;

        if (!this.wrapperElement) {
            const wrapper = document.createElement('div');
            wrapper.className = 'numeric-spin-wrapper';
            wrapper.style.position = 'relative';
            wrapper.style.flex = '1 1 auto';
            wrapper.style.minWidth = '0';
            // Bootstrap 4: .input-group > .form-control ma width: 1% – bez tego append łamie się do nowej linii
            if (hostInput.parentNode.classList && hostInput.parentNode.classList.contains('input-group')) {
                wrapper.style.width = '1%';
            }
            const computedInputStyle = getComputedStyle(hostInput);
            const display = computedInputStyle.display;
            wrapper.style.display = display === 'block' ? 'block' : 'inline-block';
            // Przechwytujemy wysokość zanim input straci regułę .input-group-sm > .form-control
            wrapper.style.height = computedInputStyle.height;

            hostInput.parentNode.insertBefore(wrapper, hostInput);
            wrapper.appendChild(hostInput);
            hostInput.style.height = '100%';
            this.wrapperElement = wrapper;
        }

        const makeButton = (label, ariaLabel, extraClass) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = `numeric-spin-btn ${extraClass}`;
            button.setAttribute('aria-label', ariaLabel);
            button.textContent = label;
            button.style.position = 'absolute';
            button.style.userSelect = 'none';
            button.style.lineHeight = '1';
            button.style.padding = '0 0.5rem';
            button.style.opacity = '0.8';
            button.style.cursor = 'pointer';
            button.style.border = 'none';
            button.style.background = 'transparent';
            button.style.borderRadius = '2px';
            button.style.display = 'flex';
            button.style.justifyContent = 'center';
            button.style.alignItems = 'center';
            return button;
        };

        if (!this.buttonUp) {
            this.buttonUp = makeButton('▲', 'Zwiększ', 'numeric-spin-up');
            this.attachSpinHandlers(this.buttonUp, +1);
            this.wrapperElement.appendChild(this.buttonUp);
        }
        if (!this.buttonDown) {
            this.buttonDown = makeButton('▼', 'Zmniejsz', 'numeric-spin-down');
            this.attachSpinHandlers(this.buttonDown, -1);
            this.wrapperElement.appendChild(this.buttonDown);
        }

        this.refreshSpinButtonsPosition();
        this.refreshSpinButtonsDisabledState();
    }

    // NIE ustawiamy .disabled; tylko kosmetyka kursora/opacity względem interaktywności pola
    refreshSpinButtonsDisabledState() {
        const globallyInteractive = this.isInteractive();
        const cursorStyle = globallyInteractive ? 'pointer' : 'not-allowed';
        const opacityStyle = globallyInteractive ? '0.8' : '0.5';

        [this.buttonUp, this.buttonDown].forEach(button => {
            if (!button) return;
            button.style.cursor = cursorStyle;
            button.style.opacity = opacityStyle;
        });
    }

    // --- pozycjonowanie
    refreshSpinButtonsPosition() {
        if (!this.buttonUp || !this.buttonDown || !this.inputElement) return;

        const { spinButtons } = this.getOptions();
        const input = this.inputElement;
        input.style.textAlign = 'center';

        // wyczyść pozycje
        ['left', 'right', 'top', 'bottom', 'height'].forEach(propName => {
            this.buttonUp.style[propName] = '';
            this.buttonDown.style[propName] = '';
        });

        const computedStyle = getComputedStyle(input);
        const inputFontSize = computedStyle.fontSize;
        this.buttonUp.style.fontSize = inputFontSize;
        this.buttonDown.style.fontSize = inputFontSize;

        if (spinButtons === 'horizontal') {
            // PLUS po prawej
            this.buttonUp.textContent = '+';
            this.buttonUp.style.top = '2px';
            this.buttonUp.style.bottom = '2px';
            this.buttonUp.style.height = 'calc(100% - 4px)';
            this.buttonUp.style.right = '2px';

            // MINUS po lewej
            this.buttonDown.textContent = '−';
            this.buttonDown.style.top = '2px';
            this.buttonDown.style.bottom = '2px';
            this.buttonDown.style.height = 'calc(100% - 4px)';
            this.buttonDown.style.left = '2px';

            const padLeft = 30;
            const padRight = 30;
            const currentLeftPadding = parseFloat(computedStyle.paddingLeft) || 0;
            const currentRightPadding = parseFloat(computedStyle.paddingRight) || 0;
            input.style.paddingLeft = `${Math.max(padLeft, currentLeftPadding)}px`;
            input.style.paddingRight = `${Math.max(padRight, currentRightPadding)}px`;
        } else {
            // VERTICAL: oba po prawej, góra/dół
            this.buttonUp.textContent = '⏶';
            this.buttonUp.style.right = '2px';
            this.buttonUp.style.top = '2px';
            this.buttonUp.style.height = '48%';

            this.buttonDown.textContent = '⏷';
            this.buttonDown.style.right = '2px';
            this.buttonDown.style.bottom = '2px';
            this.buttonDown.style.height = '48%';

            const padRight = 30;
            const currentRightPadding = parseFloat(computedStyle.paddingRight) || 0;
            input.style.paddingRight = `${Math.max(padRight, currentRightPadding)}px`;
        }
    }

    destroySpinButtons() {
        const removeNode = node => {
            if (!node) return;
            if (node._numeric_clearTimers) node._numeric_clearTimers();
            if (node.parentNode) node.parentNode.removeChild(node);
        };
        removeNode(this.buttonUp);
        removeNode(this.buttonDown);
        this.buttonUp = null;
        this.buttonDown = null;

        if (this.inputElement) {
            this.inputElement.style.paddingRight = '';
            this.inputElement.style.paddingLeft = '';
            this.inputElement.style.height = '';
        }

        if (this.wrapperElement) {
            const parent = this.wrapperElement.parentNode;
            if (parent) {
                parent.insertBefore(this.inputElement, this.wrapperElement);
                parent.removeChild(this.wrapperElement);
            }
            this.wrapperElement = null;
        }
    }

    // przytrzymanie (zatrzymanie również na blur okna)
    attachSpinHandlers(buttonElement, direction) {
        const stepOnce = () => {
            if (!this.isInteractive()) return;
            const options = this.getOptions();
            this.applyStep(direction * options.step, {
                min: options.min,
                max: options.max,
                precision: options.precision,
                isDecimal: options.isDecimal,
                normalizeTo: options.normalizeTo,
            });
        };

        const clearTimers = () => {
            if (this.holdTimer) {
                clearTimeout(this.holdTimer);
                this.holdTimer = null;
            }
            if (this.repeatTimer) {
                clearInterval(this.repeatTimer);
                this.repeatTimer = null;
            }
        };

        const onGlobalPressEnd = () => {
            clearTimers();
            document.removeEventListener('mouseup', onGlobalPressEnd);
            document.removeEventListener('touchend', onGlobalPressEnd);
            document.removeEventListener('touchcancel', onGlobalPressEnd);
            window.removeEventListener('blur', onGlobalPressEnd);
        };

        const onPressStart = event => {
            if (!this.isInteractive()) return;
            event.preventDefault();
            this.inputElement.focus();
            stepOnce();
            const { holdDelayMs, holdStepMs } = this.getOptions();
            this.holdTimer = setTimeout(() => {
                this.repeatTimer = setInterval(stepOnce, holdStepMs);
            }, holdDelayMs);

            document.addEventListener('mouseup', onGlobalPressEnd);
            document.addEventListener('touchend', onGlobalPressEnd, { passive: true });
            document.addEventListener('touchcancel', onGlobalPressEnd, { passive: true });
            window.addEventListener('blur', onGlobalPressEnd);
        };

        buttonElement.addEventListener('mousedown', onPressStart);
        buttonElement.addEventListener('touchstart', onPressStart, { passive: false });

        // Sprzątanie na destroy()
        buttonElement._numeric_clearTimers = () => {
            clearTimers();
            onGlobalPressEnd();
        };
    }

    // ---------- INPUT HANDLERS ----------
    onKeydown(event) {
        if (!this.isInteractive()) return;

        const { key, ctrlKey, metaKey, target } = event;
        const options = this.getOptions();
        const { isDecimal, isUnsigned, commaAsDecimal, dotAsDecimal, step, min, max, precision, normalizeTo } = options;

        if (key === 'ArrowUp' || key === 'Up') {
            event.preventDefault();
            this.applyStep(+step, { min, max, precision, isDecimal, normalizeTo });
            return;
        }
        if (key === 'ArrowDown' || key === 'Down') {
            event.preventDefault();
            this.applyStep(-step, { min, max, precision, isDecimal, normalizeTo });
            return;
        }
        // >>> Enter: od razu potraktuj jak 'change' (zatwierdzenie)
        if (key === 'Enter') {
            // nie blokujemy submitu formularza — tylko upewniamy się, że wartość jest zaclampowana
            this.applySanitization('change');
            return; // nie robimy preventDefault
        }

        const allowedSystemKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Esc', 'Home', 'End', 'PageUp', 'PageDown', 'ArrowLeft', 'ArrowRight', 'Left', 'Right', 'Insert', 'Del'];

        if ((ctrlKey || metaKey) && ['a', 'c', 'v', 'x', 'z', 'y', 'A', 'C', 'V', 'X', 'Z', 'Y'].includes(key)) return;
        if (allowedSystemKeys.includes(key)) return;
        if (key >= '0' && key <= '9') return;

        if (isDecimal) {
            const isDotKey = key === '.';
            const isCommaKey = key === ',';
            const currentValue = String(target.value || '');
            if ((isDotKey && dotAsDecimal) || (isCommaKey && commaAsDecimal)) {
                if (!/[.,]/.test(currentValue)) return;
            }
        }

        if (key === '-' && !isUnsigned) {
            const currentValue = String(target.value || '');
            if (!currentValue.includes('-')) return;
        }

        event.preventDefault();
    }

    onPaste() {
        // po wklejeniu — oczyść
        setTimeout(() => this.applySanitization('input'), 0);
    }

    onInput() {
        if (this.isComposing) return; // nie filtruj w trakcie składania znaku
        this.applySanitization('input');
    }

    onChange() {
        // Blur / zatwierdzenie — teraz dopiero clamp
        this.applySanitization('change');
        // Uwaga: updateValue wysyła 'input' (v-model dostaje finalną wartość).
        // Jesteśmy w natywnym 'change', więc nie emitujemy tu dodatkowego 'change'.
    }

    onCompositionStart() {
        this.isComposing = true;
    }

    onCompositionEnd() {
        this.isComposing = false;
        // po zakończeniu składu — normalizacja (bez clamp)
        this.applySanitization('input');
    }

    // ---------- STEP LOGIC ----------
    applyStep(delta, { min, max, precision, isDecimal, normalizeTo }) {
        const currentRaw = String(this.inputElement.value || '');
        const currentNumber = parseFloat(currentRaw.replace(',', '.'));
        let nextNumber = Number.isNaN(currentNumber) ? 0 : currentNumber;
        nextNumber += delta;

        if (typeof min === 'number' && nextNumber < min) nextNumber = min;
        if (typeof max === 'number' && nextNumber > max) nextNumber = max;

        let output = String(nextNumber);
        if (isDecimal && typeof precision === 'number' && precision >= 0) {
            const power = 10 ** precision;
            output = String(Math.round(nextNumber * power) / power);
        }
        if (normalizeTo === 'comma') output = output.replace('.', ',');

        if (isDecimal && typeof precision === 'number' && precision >= 0) {
            const separator = normalizeTo === 'comma' ? ',' : '.';
            const [integerPart, fractionRaw = ''] = output.split(separator);
            const trimmedFraction = fractionRaw.slice(0, precision);
            output = trimmedFraction ? `${integerPart}${separator}${trimmedFraction}` : integerPart;
        }

        this.updateValue(output);
        this.refreshSpinButtonsDisabledState();
    }

    // ---------- SANITYZACJA ----------
    applySanitization(triggerType) {
        const options = this.getOptions();
        const { isDecimal, isUnsigned, precision, min, max, commaAsDecimal, dotAsDecimal, normalizeTo } = options;

        let raw = String(this.inputElement.value || '');
        if (raw === '' || raw === '-') {
            // pozwól na pustą / sam '-' w trakcie wpisywania
            if (triggerType === 'change' && raw === '-') this.updateValue('');
            return;
        }

        const wantsNegative = /^-/.test(raw) && !isUnsigned;

        // ZAPAMIĘTAJ: czy użytkownik właśnie wpisał separator na końcu?
        const endsWithSeparatorRaw = /[.,]$/.test(raw);

        if (isDecimal) {
            const allowedSeparators = [].concat(dotAsDecimal ? ['.'] : []).concat(commaAsDecimal ? [','] : []);
            const removeInvalidRegex = new RegExp(`[^0-9${allowedSeparators.map(s => `\\${s}`).join('')}]`, 'g');
            raw = raw.replace(removeInvalidRegex, '');
        } else {
            raw = raw.replace(/\D+/g, '');
        }

        let normalized = '';
        if (isDecimal) {
            const parts = raw.split(/[.,]/);
            let integerPart = parts.shift() || '';
            let fractionPart = parts.length ? parts.pop() : '';
            if (parts.length) integerPart += parts.join(''); // nadmiarowe separatory → sklej do części całkowitej
            integerPart = integerPart.replace(/^0+(?=\d)/, '');

            const sep = normalizeTo === 'comma' ? ',' : '.';

            // Przy wpisywaniu: jeśli użytkownik dopiero wstawił separator i NIE ma jeszcze frakcji,
            // zachowaj końcowy separator, żeby było widać "12," lub "12."
            if (triggerType === 'input' && endsWithSeparatorRaw && fractionPart === '') {
                if (!integerPart) integerPart = '0'; // pozwól na ".5" -> "0."
                normalized = `${integerPart}${sep}`;
            } else {
                if (precision !== null && typeof precision === 'number') {
                    fractionPart = (fractionPart || '').slice(0, precision);
                }
                if (!integerPart && fractionPart) integerPart = '0';
                normalized = fractionPart ? `${integerPart}${sep}${fractionPart}` : integerPart;
            }
        } else {
            normalized = raw.replace(/^0+(?=\d)/, '');
        }

        if (wantsNegative && normalized !== '' && normalized !== '0' && normalized !== '0.' && normalized !== '0,') {
            normalized = `-${normalized}`;
        }

        // Jeżeli mamy pełną liczbę (a nie sam separator na końcu), można lekko znormalizować
        const looksLikeTrailingSep = /[.,]$/.test(normalized);
        const parsedNumber = parseFloat(normalized.replace(',', '.'));
        if (!Number.isNaN(parsedNumber)) {
            // przy wpisywaniu: nie psuj widocznego trailing separatora
            if (!looksLikeTrailingSep) {
                let outStr = String(parsedNumber);
                if (isDecimal && typeof precision === 'number' && precision >= 0) {
                    const power = 10 ** precision;
                    outStr = String(Math.round(parsedNumber * power) / power);
                }
                normalized = normalizeTo === 'comma' ? outStr.replace('.', ',') : outStr;

                if (isDecimal && precision !== null && typeof precision === 'number') {
                    const sep = normalizeTo === 'comma' ? ',' : '.';
                    const [i2, f2 = ''] = normalized.split(sep);
                    const trimmed2 = f2.slice(0, precision);
                    normalized = trimmed2 ? `${i2}${sep}${trimmed2}` : i2;
                }
            }

            // --- CLAMP TYLKO NA CHANGE (blur/Enter) ---
            if (triggerType === 'change') {
                let clamped = parsedNumber;
                if (typeof min === 'number' && clamped < min) clamped = min;
                if (typeof max === 'number' && clamped > max) clamped = max;

                let clampedStr = String(clamped);
                if (isDecimal && typeof precision === 'number' && precision >= 0) {
                    const power = 10 ** precision;
                    clampedStr = String(Math.round(clamped * power) / power);
                }
                normalized = normalizeTo === 'comma' ? clampedStr.replace('.', ',') : clampedStr;

                if (isDecimal && precision !== null && typeof precision === 'number') {
                    const sep = normalizeTo === 'comma' ? ',' : '.';
                    const [i3, f3 = ''] = normalized.split(sep);
                    const trimmed3 = f3.slice(0, precision);
                    normalized = trimmed3 ? `${i3}${sep}${trimmed3}` : i3;
                }
            }
        } else if (triggerType === 'change') {
            // nie da się sparsować po zatwierdzeniu — wyczyść
            normalized = '';
        }

        this.updateValue(normalized);
        this.refreshSpinButtonsDisabledState();
    }

    updateValue(nextValue) {
        if (this.inputElement.value !== nextValue) {
            this.inputElement.value = nextValue;
            // 'input' sprawi, że v-model dostanie FINALNĄ wartość (również po clampie na 'change' / Enter)
            this.inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }
}

// Poczekaj aż <input> będzie miał rodzica
function waitForMountedInput(rootElement, maxFrames = 60) {
    return new Promise((resolve, reject) => {
        let frames = 0;
        function tick() {
            const inputEl = rootElement instanceof HTMLInputElement ? rootElement : rootElement.querySelector('input');
            if (inputEl && inputEl.parentNode) {
                resolve(inputEl);
                return;
            }
            frames += 1;
            if (frames > maxFrames) {
                reject(new Error('Input not mounted (no parentNode)'));
                return;
            }
            requestAnimationFrame(tick);
        }
        tick();
    });
}

const NumericDirective = {
    install(Vue) {
        const hooks = {
            async inserted(rootElement, binding) {
                try {
                    const inputEl = await waitForMountedInput(rootElement, 120);
                    if (inputEl.type !== 'text') inputEl.setAttribute('type', 'text');
                    inputEl.setAttribute('inputmode', 'decimal');
                    inputEl.setAttribute('autocomplete', 'off');

                    // Jeśli update() został wywołany zanim inserted() skończyło async —
                    // użyj najnowszego bindingu zamiast pierwotnego
                    const latestBinding = pendingBindings.get(rootElement) || binding;
                    pendingBindings.delete(rootElement);

                    const instance = new NumericDirectiveInstance(inputEl, latestBinding);
                    directiveInstances.set(rootElement, instance);
                } catch (e) {
                    // opcjonalnie: console.warn('numeric directive: input not mounted', e);
                }
            },
            update(rootElement, binding) {
                const instance = directiveInstances.get(rootElement);
                if (instance) {
                    instance.updateBinding(binding);
                } else {
                    // inserted() jeszcze nie skończyło — zapamiętaj najnowszy binding
                    pendingBindings.set(rootElement, binding);
                }
            },
            unbind(rootElement) {
                const instance = directiveInstances.get(rootElement);
                if (instance) {
                    instance.destroy();
                    directiveInstances.delete(rootElement);
                }
                pendingBindings.delete(rootElement);
            },
        };

        Vue.directive('decimal', hooks);
        Vue.directive('integer', hooks);
    },
};

export default NumericDirective;
